import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../../services/axiosInstance';

// Tool type for switching between ImageMagick and Inkscape
type ToolType = 'imagemagick' | 'inkscape';

// Command building blocks that can be combined
interface CommandBlock {
  label: string;
  code: string;
  category: 'base' | 'transparency' | 'color' | 'size' | 'quality' | 'other';
  description?: string;
}

const commandBlocks: CommandBlock[] = [
  // Base commands (choose one)
  { label: 'Vektor (EPS/AI/PDF)', code: '-density 300 {input}[0]', category: 'base', description: 'Läser vektorfil med 300 DPI' },
  { label: 'Raster (PNG/JPG)', code: '{input}', category: 'base', description: 'Läser rasterbild' },
  
  // Transparency options
  { label: 'Transparent bakgrund', code: '-background none', category: 'transparency', description: 'Sätter bakgrunden till transparent' },
  { label: 'Vit bakgrund', code: '-background white -flatten', category: 'transparency', description: 'Plattar bilden med vit bakgrund' },
  { label: 'Alpha på', code: '-alpha on', category: 'transparency', description: 'Aktiverar alpha-kanal' },
  { label: 'Alpha set', code: '-alpha set', category: 'transparency', description: 'Sätter alpha från transparens' },
  { label: 'Ta bort vit (fuzz 5%)', code: '-fuzz 5% -transparent white', category: 'transparency', description: 'Gör vita pixlar transparenta' },
  
  // Color options
  { label: 'sRGB (enkel)', code: '-colorspace sRGB', category: 'color', description: 'Enkel matematisk konvertering till sRGB' },
  { label: 'sRGB + ICC profil', code: '-profile "C:\\Windows\\System32\\spool\\drivers\\color\\sRGB Color Space Profile.icm"', category: 'color', description: 'Använd Windows sRGB ICC-profil' },
  { label: 'Intent: Perceptual', code: '-intent perceptual -colorspace sRGB', category: 'color', description: 'Bäst för foton, bevarar färgrelationer' },
  { label: 'Intent: Relative', code: '-intent relative -colorspace sRGB', category: 'color', description: 'Bäst för logotyper, exakta färger' },
  { label: 'Intent: Saturation', code: '-intent saturation -colorspace sRGB', category: 'color', description: 'Mättade/livfulla färger' },
  { label: 'CMYK→sRGB (behåll profil)', code: '-colorspace sRGB', category: 'color', description: 'Konvertera CMYK men behåll inbäddad profil' },
  { label: 'Strip metadata', code: '-strip', category: 'color', description: 'Ta bort all metadata inkl ICC-profiler' },
  { label: '8-bit djup', code: '-depth 8', category: 'color', description: 'Sätt färgdjup till 8-bit' },
  
  // Size options
  { label: 'Max 1500px', code: '-resize 1500x1500', category: 'size', description: 'Max 1500px på längsta sidan' },
  { label: 'Max 1920px', code: '-resize 1920x1920', category: 'size', description: 'Max 1920px på längsta sidan' },
  { label: 'Max 800px', code: '-resize 800x800', category: 'size', description: 'Max 800px på längsta sidan' },
  { label: 'Trim (ta bort kant)', code: '-trim +repage', category: 'size', description: 'Trimmar bort tomma kanter' },
  
  // Quality/format
  { label: 'PNG kvalitet 90', code: '-quality 90', category: 'quality', description: 'PNG kompression' },
  { label: 'JPG kvalitet 85', code: '-quality 85', category: 'quality', description: 'JPG kvalitet' },
];

// Pre-made complete commands for ImageMagick
const presetCommands = [
  { 
    label: '📦 Enkel EPS → PNG', 
    cmd: '-density 300 {input}[0] {output}',
    description: 'Konverterar EPS/AI till PNG utan färgjustering'
  },
  { 
    label: '🎨 CMYK→sRGB (perceptual)', 
    cmd: '-density 300 {input}[0] -intent perceptual -colorspace sRGB -depth 8 {output}',
    description: 'Färgkonvertering med perceptual intent - bäst för foton'
  },
  { 
    label: '🎯 CMYK→sRGB (relative)', 
    cmd: '-density 300 {input}[0] -intent relative -colorspace sRGB -depth 8 {output}',
    description: 'Färgkonvertering med relative intent - bäst för logotyper'
  },
  { 
    label: '💎 CMYK→sRGB (saturation)', 
    cmd: '-density 300 {input}[0] -intent saturation -colorspace sRGB -depth 8 {output}',
    description: 'Färgkonvertering med saturation intent - mättade färger'
  },
  { 
    label: '🔧 CMYK→sRGB + ICC profil', 
    cmd: '-density 300 {input}[0] -profile "C:\\Windows\\System32\\spool\\drivers\\color\\sRGB Color Space Profile.icm" -depth 8 {output}',
    description: 'Använder Windows ICC-profil för konvertering'
  },
  { 
    label: '✨ EPS transparent bakgrund', 
    cmd: '-density 300 -background none {input}[0] -alpha on {output}',
    description: 'Behåller transparens från vektorfil'
  },
  { 
    label: '🖼️ Illustration (DAM standard)', 
    cmd: '-density 300 {input}[0] -intent perceptual -colorspace sRGB -background none -alpha set -resize 1500x1500 -depth 8 {output}',
    description: 'Samma kommando som DAM använder för illustrationer'
  },
  { 
    label: '📐 EPS trimma + resize', 
    cmd: '-density 300 {input}[0] -trim +repage -resize 1500x1500 {output}',
    description: 'Tar bort kanter och ändrar storlek'
  },
  { 
    label: '⬜ EPS med vit bakgrund', 
    cmd: '-density 300 {input}[0] -background white -flatten -resize 1500x1500 {output}',
    description: 'Plattar transparens mot vit bakgrund'
  },
  { 
    label: '🔍 Ta bort vit bakgrund', 
    cmd: '-density 300 {input}[0] -fuzz 5% -transparent white {output}',
    description: 'Gör vita pixlar transparenta (varning: även vitt i bilden!)'
  },
];

// Pre-made complete commands for Inkscape
const inkscapePresetCommands = [
  { 
    label: '📦 Enkel AI/EPS → PNG', 
    cmd: '{input} --export-type=png --export-filename={output}',
    description: 'Enkel konvertering till PNG'
  },
  { 
    label: '🎯 PNG med 300 DPI', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-filename={output}',
    description: 'Exportera med 300 DPI upplösning'
  },
  { 
    label: '✨ Transparent PNG 300 DPI', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-background-opacity=0 --export-filename={output}',
    description: 'PNG med transparent bakgrund - bäst för AI-filer!'
  },
  { 
    label: '📐 Max 1500px bredd', 
    cmd: '{input} --export-type=png --export-width=1500 --export-background-opacity=0 --export-filename={output}',
    description: 'Exportera med max 1500 pixlar bredd'
  },
  { 
    label: '📐 Max 1920px bredd', 
    cmd: '{input} --export-type=png --export-width=1920 --export-background-opacity=0 --export-filename={output}',
    description: 'Exportera med max 1920 pixlar bredd'
  },
  { 
    label: '🖼️ Illustration (rekommenderad)', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-width=1500 --export-background-opacity=0 --export-filename={output}',
    description: 'Bästa inställning för AI/EPS illustrationer med transparens'
  },
  { 
    label: '⬜ PNG med vit bakgrund', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-background=white --export-filename={output}',
    description: 'PNG med vit bakgrund istället för transparent'
  },
  { 
    label: '🎨 Endast synligt område', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-area-drawing --export-background-opacity=0 --export-filename={output}',
    description: 'Beskär till det synliga innehållet'
  },
  { 
    label: '📄 Hela sidan', 
    cmd: '{input} --export-type=png --export-dpi=300 --export-area-page --export-background-opacity=0 --export-filename={output}',
    description: 'Exportera hela dokumentsidan'
  },
];

const ImageMagickTestPage: React.FC = () => {
  const [activeTool, setActiveTool] = useState<ToolType>('imagemagick');
  const [file, setFile] = useState<File | null>(null);
  const [command, setCommand] = useState<string>('-density 300 {input}[0] {output}');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [magickInfo, setMagickInfo] = useState<any>(null);
  const [inkscapeInfo, setInkscapeInfo] = useState<any>(null);
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [previewBg, setPreviewBg] = useState<'checker' | 'black' | 'white' | 'magenta'>('checker');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch tool info on mount
  useEffect(() => {
    const fetchMagickInfo = async () => {
      try {
        const { data } = await axiosInstance.get('/api/settings/imagemagick-test/info');
        setMagickInfo(data);
      } catch (err) {
        console.error('Failed to get ImageMagick info:', err);
      }
    };
    const fetchInkscapeInfo = async () => {
      try {
        const { data } = await axiosInstance.get('/api/settings/inkscape-test/info');
        setInkscapeInfo(data);
      } catch (err) {
        console.error('Failed to get Inkscape info:', err);
      }
    };
    fetchMagickInfo();
    fetchInkscapeInfo();
  }, []);

  // Reset command when switching tools
  const handleToolChange = (_event: React.SyntheticEvent, newValue: ToolType) => {
    setActiveTool(newValue);
    setResult(null);
    setError(null);
    setSelectedBlocks([]);
    // Set default command for the selected tool
    if (newValue === 'imagemagick') {
      setCommand('-density 300 {input}[0] {output}');
    } else {
      setCommand('{input} --export-type=png --export-dpi=300 --export-background-opacity=0 --export-filename={output}');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleRunTest = async () => {
    if (!file) {
      setError('Välj en fil först');
      return;
    }
    if (!command.trim()) {
      setError(`Skriv ett ${activeTool === 'imagemagick' ? 'ImageMagick' : 'Inkscape'}-kommando`);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('command', command);

      const endpoint = activeTool === 'imagemagick' 
        ? '/api/settings/imagemagick-test' 
        : '/api/settings/inkscape-test';

      const { data } = await axiosInstance.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(data);
    } catch (err: any) {
      const errData = err.response?.data;
      if (errData) {
        setResult(errData);
        setError(errData.message || 'Kommandot misslyckades');
      } else {
        setError(err.message || 'Ett fel uppstod');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.output?.filename) return;
    
    try {
      const endpoint = activeTool === 'imagemagick'
        ? `/api/settings/imagemagick-test/download/${result.output.filename}`
        : `/api/settings/inkscape-test/download/${result.output.filename}`;

      const response = await axiosInstance.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.output.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Kunde inte ladda ner filen');
    }
  };

  const copyCommand = () => {
    if (result?.command) {
      navigator.clipboard.writeText(result.command);
    }
  };

  // Add a command block to the current command
  const addBlock = (block: CommandBlock) => {
    // Check if block is already selected
    if (selectedBlocks.includes(block.code)) {
      // Remove it
      setSelectedBlocks(prev => prev.filter(b => b !== block.code));
    } else {
      // Add it
      setSelectedBlocks(prev => [...prev, block.code]);
    }
  };

  // Build command from selected blocks
  const buildCommandFromBlocks = () => {
    if (selectedBlocks.length === 0) return;
    
    // Find if we have a base command
    const baseBlocks = commandBlocks.filter(b => b.category === 'base');
    const hasBase = selectedBlocks.some(code => baseBlocks.some(b => b.code === code));
    
    // Build the command
    let parts: string[] = [];
    
    // Add base first (or default)
    const baseCode = selectedBlocks.find(code => baseBlocks.some(b => b.code === code));
    if (baseCode) {
      parts.push(baseCode);
    } else {
      parts.push('-density 300 {input}[0]');
    }
    
    // Add other selected blocks (not base)
    selectedBlocks.forEach(code => {
      if (!baseBlocks.some(b => b.code === code)) {
        parts.push(code);
      }
    });
    
    // Add output
    parts.push('{output}');
    
    setCommand(parts.join(' '));
  };

  // Reset to default
  const resetCommand = () => {
    if (activeTool === 'imagemagick') {
      setCommand('-density 300 {input}[0] {output}');
    } else {
      setCommand('{input} --export-type=png --export-dpi=300 --export-background-opacity=0 --export-filename={output}');
    }
    setSelectedBlocks([]);
  };

  // Apply a preset command
  const applyPreset = (preset: typeof presetCommands[0]) => {
    setCommand(preset.cmd);
    setSelectedBlocks([]);
  };

  const getCategoryColor = (category: CommandBlock['category']) => {
    switch (category) {
      case 'base': return 'primary';
      case 'transparency': return 'secondary';
      case 'color': return 'success';
      case 'size': return 'warning';
      case 'quality': return 'info';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: CommandBlock['category']) => {
    switch (category) {
      case 'base': return '📄 Bas (input)';
      case 'transparency': return '✨ Transparens';
      case 'color': return '🎨 Färg';
      case 'size': return '📐 Storlek';
      case 'quality': return '⚙️ Kvalitet';
      default: return 'Övrigt';
    }
  };

  // Get current presets based on active tool
  const currentPresets = activeTool === 'imagemagick' ? presetCommands : inkscapePresetCommands;
  const currentToolInfo = activeTool === 'imagemagick' ? magickInfo : inkscapeInfo;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Konverteringsverktyg
      </Typography>
      
      {/* Tool Selection Tabs */}
      <Tabs 
        value={activeTool} 
        onChange={handleToolChange} 
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          label="ImageMagick" 
          value="imagemagick"
          sx={{ fontWeight: activeTool === 'imagemagick' ? 600 : 400 }}
        />
        <Tab 
          label="Inkscape" 
          value="inkscape"
          sx={{ fontWeight: activeTool === 'inkscape' ? 600 : 400 }}
        />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {activeTool === 'imagemagick' ? (
          <>
            Testa ImageMagick-kommandon direkt i systemet. Använd färdiga mallar eller bygg eget kommando.
            <br />
            <code>{'{input}'}</code> = din fil, <code>{'{output}'}</code> = resultatfil, <code>[0]</code> = första sidan (krävs för EPS/AI/PDF)
          </>
        ) : (
          <>
            Testa Inkscape-kommandon för vektorkonvertering. <strong>Rekommenderas för AI-filer!</strong>
            <br />
            <code>{'{input}'}</code> = din fil, <code>{'{output}'}</code> = resultatfil
          </>
        )}
      </Typography>

      {/* Tool Info */}
      {currentToolInfo && (
        <Alert 
          severity={currentToolInfo.success ? 'success' : 'warning'} 
          sx={{ mb: 2 }}
          icon={<InfoIcon />}
        >
          {currentToolInfo.success ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {activeTool === 'imagemagick' ? 'ImageMagick' : 'Inkscape'} hittades:
              </Typography>
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {currentToolInfo.version?.split('\n').slice(0, 2).join('\n')}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2">
              {activeTool === 'imagemagick' ? 'ImageMagick' : 'Inkscape'} kunde inte hittas på:{' '}
              {currentToolInfo.magickPath || currentToolInfo.inkscapePath}
            </Typography>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* File Upload */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>1. Välj fil (EPS, AI, PDF, PNG, JPG...)</Typography>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".eps,.ai,.pdf,.png,.jpg,.jpeg,.tiff,.svg"
          />
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Välj fil
          </Button>
          {file && (
            <Chip 
              label={file.name} 
              onDelete={() => setFile(null)} 
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Preset Commands */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>2. Välj färdig mall (klicka för att använda)</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {currentPresets.map((preset, i) => (
              <Tooltip key={i} title={preset.description} arrow>
                <Chip
                  label={preset.label}
                  size="small"
                  variant="outlined"
                  color={activeTool === 'imagemagick' ? 'primary' : 'secondary'}
                  onClick={() => applyPreset(preset)}
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Command Building Blocks - Only for ImageMagick */}
        {activeTool === 'imagemagick' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                3. Eller bygg eget kommando (klicka för att lägga till/ta bort)
              </Typography>
              
              {(['base', 'transparency', 'color', 'size', 'quality'] as const).map(category => (
                <Box key={category} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {getCategoryLabel(category)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {commandBlocks.filter(b => b.category === category).map((block, i) => (
                      <Tooltip key={i} title={block.description || ''} arrow>
                        <Chip
                          label={block.label}
                          size="small"
                          color={selectedBlocks.includes(block.code) ? getCategoryColor(category) : 'default'}
                          variant={selectedBlocks.includes(block.code) ? 'filled' : 'outlined'}
                          onClick={() => addBlock(block)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              ))}
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={buildCommandFromBlocks}
                  disabled={selectedBlocks.length === 0}
                >
                  Bygg kommando
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<RestartAltIcon />}
                  onClick={resetCommand}
                >
                  Återställ
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Command Input */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {activeTool === 'imagemagick' ? '4. Aktuellt kommando (kan redigeras manuellt)' : '3. Aktuellt kommando (kan redigeras manuellt)'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder={activeTool === 'imagemagick' 
              ? '-density 300 {input}[0] {output}' 
              : '{input} --export-type=png --export-dpi=300 --export-filename={output}'}
            sx={{ 
              '& .MuiInputBase-input': { 
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              } 
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Run Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleRunTest}
            disabled={loading || !file}
            size="large"
          >
            {loading ? 'Kör...' : 'Kör kommando'}
          </Button>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Result */}
      {result && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Resultat
            {result.success ? (
              <Chip label="OK" color="success" size="small" sx={{ ml: 1 }} />
            ) : (
              <Chip label="FEL" color="error" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>

          {/* Executed Command */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Kört kommando:</Typography>
            <Box sx={{ 
              backgroundColor: '#1e1e1e', 
              color: '#d4d4d4', 
              p: 1.5, 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              overflowX: 'auto',
              position: 'relative'
            }}>
              <code>{result.command}</code>
              <Tooltip title="Kopiera kommando">
                <IconButton 
                  size="small" 
                  onClick={copyCommand}
                  sx={{ position: 'absolute', top: 4, right: 4, color: '#888' }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Stdout/Stderr */}
          {(result.stdout || result.stderr || result.error) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Output:</Typography>
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1.5, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                maxHeight: 150,
                overflowY: 'auto'
              }}>
                {result.stdout && <pre style={{ margin: 0 }}>{result.stdout}</pre>}
                {result.stderr && <pre style={{ margin: 0, color: '#c00' }}>{result.stderr}</pre>}
                {result.error && <pre style={{ margin: 0, color: '#c00' }}>{result.error}</pre>}
              </Box>
            </Box>
          )}

          {/* Preview & Download */}
          {result.success && result.output && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview ({(result.output.size / 1024).toFixed(1)} KB):
              </Typography>
              
              {/* Background toggle buttons */}
              <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  Bakgrund för att se transparens:
                </Typography>
                <Chip 
                  label="🏁 Schack" 
                  size="small" 
                  variant={previewBg === 'checker' ? 'filled' : 'outlined'}
                  color={previewBg === 'checker' ? 'primary' : 'default'}
                  onClick={() => setPreviewBg('checker')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip 
                  label="⬛ Svart" 
                  size="small" 
                  variant={previewBg === 'black' ? 'filled' : 'outlined'}
                  color={previewBg === 'black' ? 'primary' : 'default'}
                  onClick={() => setPreviewBg('black')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip 
                  label="⬜ Vit" 
                  size="small" 
                  variant={previewBg === 'white' ? 'filled' : 'outlined'}
                  color={previewBg === 'white' ? 'primary' : 'default'}
                  onClick={() => setPreviewBg('white')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip 
                  label="🟪 Magenta" 
                  size="small" 
                  variant={previewBg === 'magenta' ? 'filled' : 'outlined'}
                  color={previewBg === 'magenta' ? 'primary' : 'default'}
                  onClick={() => setPreviewBg('magenta')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>

              <Box sx={{ 
                border: '1px solid #ddd', 
                borderRadius: 1, 
                p: 2, 
                backgroundColor: '#fafafa',
                textAlign: 'center',
                mb: 2
              }}>
                {/* Dynamic background based on selection */}
                <Box sx={{
                  display: 'inline-block',
                  ...(previewBg === 'checker' && {
                    backgroundImage: 'linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)',
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                    backgroundColor: '#c0c0c0',
                  }),
                  ...(previewBg === 'black' && {
                    backgroundColor: '#000000',
                  }),
                  ...(previewBg === 'white' && {
                    backgroundColor: '#ffffff',
                  }),
                  ...(previewBg === 'magenta' && {
                    backgroundColor: '#ff00ff',
                  }),
                }}>
                  <img 
                    src={result.output.base64} 
                    alt="Result" 
                    style={{ maxWidth: '100%', maxHeight: 400, display: 'block' }}
                  />
                </Box>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                💡 Tips: Växla bakgrund för att se om bilden har transparent bakgrund. 
                Om bilden ser likadan ut på alla bakgrunder = ingen transparens.
              </Typography>
              
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                Ladda ner resultat
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ImageMagickTestPage;
